import { computed, ref, watch } from '@vue/composition-api';
import { diff } from 'deep-object-diff';
import stateProviderSetup from 'mixins/composition/search/StateProvider';

/**
 *
 * need this logic to prevent hydration error when a user uses the search form within hero images to search into a not yet hydrated object result set
 * since search results are lazy-hydrated, they have to get hydrated before another search is performed
 *
 */

const initialSearchTerms = ref({});
const hydrated = ref({});
const mounted = ref({});

// this promise resolves once all watched components got hydrated abnd mounted
// useful if you have to wait for hydrate to fully complete
let mountWatchResolver;
const searchObjectsMounted = new Promise((res) => {
  mountWatchResolver = res;
});

export default (store, app, refs) => {
  const { showModalStatus, fullscreenmap } = stateProviderSetup({ app, store });
  const mergedTerms = computed(() => store.getters.getMergedTerms);

  // set initial search terms when search is loaded
  const setInitialSearchTerms = () => {
    initialSearchTerms.value = {
      ...store.getters.getMergedTerms,
    };
    if (store.getters.getLocationName) {
      initialSearchTerms.value.q = store.getters.getLocationName;
    }
  };

  // this is done within components that alter the search form and hence indicate that the user might perform a search soon
  // => hydrate the server result set to prevent hydration errors
  const dispatchHydrateAction = () => {
    if (Object.keys(hydrated.value).map((el) => hydrated.value[el]).filter((el) => el !== true).length > 0) store.dispatch('HYDRATE_SEARCH_OBJECTS', true);
  };

  // this is evoked inside components to have a signal when they are actually hydrated
  const dispatchMountSignal = (el) => {
    store.dispatch('SEARCH_OBJECTS_MOUNTED', el);
  };

  // checks whether the original form was modified
  // reason is that if the first action is clicking the search button, hydration errors will still occur, because we have no chance to hydrate the results in time
  // as workaround we just scroll to the result set (the performed search would be redundant anyway)
  const shouldSearch = () => {
    // always search when not on search page
    if (app.$route.name.match(/search/i) === null) return true;
    if (Object.keys(diff(initialSearchTerms.value, store.getters.getMergedTerms)).length > 0) {
      initialSearchTerms.value = {};
      return true;
    }

    return false;
  };

  // watch the required search terms for changes and fire hydration action in case
  watch(mergedTerms, (value) => {
    if ((value.q !== initialSearchTerms.value.q)
        || (value.persons !== initialSearchTerms.value.persons)
        || (value.startDate !== initialSearchTerms.value.startDate)
    ) {
      dispatchHydrateAction();
    }
  });

  watch(showModalStatus, (value) => { if (value) dispatchHydrateAction(); });
  watch(fullscreenmap, (value) => { if (value) dispatchHydrateAction(); });

  // this function is picked up by Search.vue to watch for the hydration action
  // subscribe in mounted
  const watchHydration = (elToHydrate) => {
    hydrated.value[elToHydrate] = false;
    mounted.value[elToHydrate] = false;

    store.subscribeAction((action) => {
      // set hydrated to true to prevent unecessary future dispatches of action
      if (action.type === 'HYDRATE_SEARCH_OBJECTS' && action.payload === true && hydrated.value[elToHydrate] === false) {
        if (typeof refs[elToHydrate]?.hydrate === 'function') {
          refs[elToHydrate].hydrationPromise.finally(() => {
            hydrated.value[elToHydrate] = true;
          });
          refs[elToHydrate].hydrate();
        }
      }

      // subscribe to action that checks for mounted components
      if (action.type === 'SEARCH_OBJECTS_MOUNTED' && action.payload === elToHydrate) {
        mounted.value[elToHydrate] = true;
        // check if all items have been mounted and then resolve the Promise
        if (Object.keys(mounted.value).map((el) => mounted.value[el]).filter((el) => el !== true).length === 0) mountWatchResolver();
      }
    });
  };

  return {
    setInitialSearchTerms, shouldSearch, watchHydration, dispatchHydrateAction, dispatchMountSignal, searchObjectsMounted,
  };
};
